/**
 * STORE-V2 / VISUAL-SEARCH-001-R7 — cuisine/country discovery for Asia-store positioning.
 * Primary customer meaning: cuisine / taste origin inferred from catalog titles.
 * Secondary: brand manufacturer country (metadata only — not the directory default).
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { CountryIndexRow } from "./catalog-v2-data.js";

export type CountryMeaning =
  | "cuisine_country"
  | "brand_country"
  | "manufacturing_country"
  | "market_positioning_country";

/** Customer-facing default for the directory and filters. */
export const DEFAULT_COUNTRY_FILTER_MEANING: CountryMeaning = "cuisine_country";

export interface CatalogCountry {
  country_id: string;
  label: string;
  labelUk: string;
  labelEn: string;
  /** ISO 3166-1 alpha-2 for local/emoji flag mapping (no CDN hotlink). */
  iso2: string | null;
  flagEmoji: string;
  /** Optional local asset URL — null until packaged flags land; emoji is primary. */
  flagUrl: string | null;
  meaning: CountryMeaning;
  productCount: number;
  activeAvailabilityCount: number;
}

type CuisineRule = {
  id: string;
  iso2: string;
  labelUk: string;
  labelEn: string;
  flagEmoji: string;
  /** Match against title/category haystack (lowercase). */
  patterns: RegExp[];
  /** Prefer Asia storefront order. */
  priority: number;
};

const CUISINE_RULES: CuisineRule[] = [
  { id: "japan", iso2: "jp", labelUk: "Японія", labelEn: "Japan", flagEmoji: "🇯🇵", priority: 100, patterns: [/япон/i, /\bjapan\b/i, /\bjapanese\b/i, /васабі/i, /місо/i, /\bnori\b/i, /саке/i, /рамен/i, /matcha/i, /матча/i] },
  { id: "south-korea", iso2: "kr", labelUk: "Південна Корея", labelEn: "South Korea", flagEmoji: "🇰🇷", priority: 95, patterns: [/коре/i, /\bkorea\b/i, /\bkorean\b/i, /кімчі/i, /кимчи/i, /гочуджан/i, /gochujang/i, /самгь/i] },
  { id: "china", iso2: "cn", labelUk: "Китай", labelEn: "China", flagEmoji: "🇨🇳", priority: 90, patterns: [/китай/i, /\bchina\b/i, /\bchinese\b/i, /сичуан/i, /сичуань/i, /хого/i, /тофу/i] },
  { id: "thailand", iso2: "th", labelUk: "Таїланд", labelEn: "Thailand", flagEmoji: "🇹🇭", priority: 88, patterns: [/таїланд/i, /таиланд/i, /\bthai\b/i, /паста\s*том/i, /том\s*ям/i, /каррі\s*таї/i] },
  { id: "vietnam", iso2: "vn", labelUk: "Вʼєтнам", labelEn: "Vietnam", flagEmoji: "🇻🇳", priority: 86, patterns: [/в['ʼ`]?єтнам/i, /\bvietnam\b/i, /\bvietnamese\b/i, /фо\s*бо/i, /nuoc\s*mam/i, /рисова\s*локшина/i] },
  { id: "singapore", iso2: "sg", labelUk: "Сінгапур", labelEn: "Singapore", flagEmoji: "🇸🇬", priority: 85, patterns: [/сінгапур/i, /сингапур/i, /\bsingapore\b/i] },
  { id: "taiwan", iso2: "tw", labelUk: "Тайвань", labelEn: "Taiwan", flagEmoji: "🇹🇼", priority: 84, patterns: [/тайван/i, /\btaiwan\b/i] },
  { id: "malaysia", iso2: "my", labelUk: "Малайзія", labelEn: "Malaysia", flagEmoji: "🇲🇾", priority: 82, patterns: [/малайз/i, /\bmalaysia\b/i] },
  { id: "indonesia", iso2: "id", labelUk: "Індонезія", labelEn: "Indonesia", flagEmoji: "🇮🇩", priority: 80, patterns: [/індонез/i, /\bindonesia\b/i, /tempeh/i, /sambal/i] },
  { id: "philippines", iso2: "ph", labelUk: "Філіппіни", labelEn: "Philippines", flagEmoji: "🇵🇭", priority: 78, patterns: [/філіпін/i, /филиппин/i, /\bphilippine/i, /\bphilippines\b/i] },
  { id: "india", iso2: "in", labelUk: "Індія", labelEn: "India", flagEmoji: "🇮🇳", priority: 70, patterns: [/інді/i, /\bindia\b/i, /\bindian\b/i, /масала/i, /каррі/i] },
  { id: "hong-kong", iso2: "hk", labelUk: "Гонконг", labelEn: "Hong Kong", flagEmoji: "🇭🇰", priority: 68, patterns: [/гонконг/i, /\bhong\s*kong\b/i] },
];

const MANUFACTURER_NOISE: RegExp[] = [
  /німеччин/i,
  /\bgermany\b/i,
  /франц/i,
  /\bfrance\b/i,
  /\busa\b/i,
  /сша/i,
  /польщ/i,
  /\bpoland\b/i,
  /італі/i,
  /\bitaly\b/i,
];

function haystack(product: CatalogV2Product): string {
  return [product.title, product.categoryName, product.brand, product.country, ...(product.categoryKeys ?? [])]
    .filter(Boolean)
    .join(" ");
}

/** Infer cuisine country id from product text — ignores EU manufacturer noise when Asia cue exists. */
export function inferCuisineCountryId(product: CatalogV2Product): string | null {
  const hay = haystack(product);
  let best: CuisineRule | null = null;
  for (const rule of CUISINE_RULES) {
    if (rule.patterns.some((re) => re.test(hay))) {
      if (!best || rule.priority > best.priority) best = rule;
    }
  }
  if (best) return best.id;
  // Explicit product.country if it maps to a cuisine id
  const raw = String(product.country ?? "").trim();
  if (raw) {
    const id = raw
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase("en")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (CUISINE_RULES.some((r) => r.id === id || r.labelEn.toLowerCase() === raw.toLowerCase())) {
      return CUISINE_RULES.find((r) => r.id === id || r.labelEn.toLowerCase() === raw.toLowerCase())!.id;
    }
  }
  void MANUFACTURER_NOISE;
  return null;
}

function ruleById(id: string): CuisineRule | undefined {
  return CUISINE_RULES.find((r) => r.id === id);
}

export function countryFlagEmoji(idOrIso2: string): string {
  const rule = ruleById(idOrIso2);
  if (rule) return rule.flagEmoji;
  const iso = String(idOrIso2 || "")
    .trim()
    .toUpperCase();
  if (/^[A-Z]{2}$/.test(iso)) {
    const base = 127397;
    return String.fromCodePoint(base + iso.charCodeAt(0), base + iso.charCodeAt(1));
  }
  return "🌏";
}

/** Local emoji flags only — no runtime hotlink to flag CDN (STAGING-LAUNCH-READINESS-002). */
export function countryFlagUrl(_iso2: string | null): string | null {
  return null;
}

function normalizeCountryKey(value: string): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("en")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const COUNTRY_LABEL_ALIASES: Record<string, string> = {
  japan: "jp",
  "south-korea": "kr",
  korea: "kr",
  china: "cn",
  thailand: "th",
  vietnam: "vn",
  taiwan: "tw",
  indonesia: "id",
  philippines: "ph",
  malaysia: "my",
  singapore: "sg",
  india: "in",
  "hong-kong": "hk",
  ukraine: "ua",
  germany: "de",
  france: "fr",
  italy: "it",
  poland: "pl",
  netherlands: "nl",
  usa: "us",
  "united-states": "us",
  uk: "gb",
  "united-kingdom": "gb",
};

/** Map free-text brand / manufacturer country label → ISO2 for flags. */
export function countryCodeFromLabel(label: string): string | null {
  const key = normalizeCountryKey(label);
  if (!key) return null;
  if (/^[a-z]{2}$/.test(key)) return key;
  const fromAlias = COUNTRY_LABEL_ALIASES[key];
  if (fromAlias) return fromAlias;
  for (const rule of CUISINE_RULES) {
    if (normalizeCountryKey(rule.labelUk) === key || normalizeCountryKey(rule.labelEn) === key || rule.id === key) {
      return rule.iso2;
    }
  }
  return null;
}

export function countryFlagForLabel(label: string): { code: string | null; emoji: string; url: string | null } {
  const code = countryCodeFromLabel(label);
  if (!code) return { code: null, emoji: "🏷", url: null };
  return { code, emoji: countryFlagEmoji(code), url: countryFlagUrl(code) };
}

export function buildCountryIndex(
  products: CatalogV2Product[],
  meaning: CountryMeaning = DEFAULT_COUNTRY_FILTER_MEANING,
): CatalogCountry[] {
  const buckets = new Map<string, { count: number; active: number }>();
  for (const product of products) {
    const id =
      meaning === "cuisine_country" || meaning === "market_positioning_country"
        ? inferCuisineCountryId(product)
        : product.country
          ? String(product.country)
              .toLocaleLowerCase("en")
              .replace(/[^a-z0-9]+/g, "-")
          : null;
    if (!id) continue;
    const rule = ruleById(id);
    if (!rule && meaning === "cuisine_country") continue;
    const cur = buckets.get(id) ?? { count: 0, active: 0 };
    cur.count += 1;
    if (product.isSellable && product.stockStatus !== "out_of_stock") cur.active += 1;
    buckets.set(id, cur);
  }
  return [...buckets.entries()]
    .map(([id, row]) => {
      const rule = ruleById(id);
      const iso2 = rule?.iso2 ?? null;
      return {
        country_id: id,
        label: rule?.labelEn ?? id,
        labelUk: rule?.labelUk ?? id,
        labelEn: rule?.labelEn ?? id,
        iso2,
        flagEmoji: rule?.flagEmoji ?? "🌏",
        flagUrl: countryFlagUrl(iso2),
        meaning,
        productCount: row.count,
        activeAvailabilityCount: row.active,
      };
    })
    .filter((c) => c.productCount > 0)
    .sort((a, b) => {
      const pa = ruleById(a.country_id)?.priority ?? 0;
      const pb = ruleById(b.country_id)?.priority ?? 0;
      return pb - pa || b.productCount - a.productCount || a.label.localeCompare(b.label, "uk");
    });
}

export function mapCountryIndexRows(rows: CountryIndexRow[]): CatalogCountry[] {
  return rows.map((row) => {
    const iso2 = row.iso2 ? String(row.iso2).toLowerCase() : null;
    return {
      country_id: row.country_id,
      label: row.label || row.labelEn || row.country_id,
      labelUk: row.labelUk || row.label,
      labelEn: row.labelEn || row.label,
      iso2,
      flagEmoji: row.flagEmoji || "🌏",
      flagUrl: countryFlagUrl(iso2),
      meaning: (row.meaning as CountryMeaning) || DEFAULT_COUNTRY_FILTER_MEANING,
      productCount: row.productCount,
      activeAvailabilityCount: row.activeAvailabilityCount,
    };
  });
}

export function productsForCountry(
  products: CatalogV2Product[],
  countryIdValue: string,
): CatalogV2Product[] {
  return products.filter((product) => {
    if (!product.isSellable) return false;
    return inferCuisineCountryId(product) === countryIdValue;
  });
}
